import type { Stage } from '@/domain/creature/types';
import {
  ATTENTION_THRESHOLD,
  DISCIPLINE_BUMP,
  DISCIPLINE_DECAY_MS,
  TANTRUM_TRIGGER_MS,
} from './constants';

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

export interface DisciplineState {
  discipline: number;
  tantrum: boolean;
  tantrumStartedAt: number | null;
  attentionLowSince: number | null;
}

export interface DisciplineTickInput {
  state: DisciplineState;
  hunger: number;
  happiness: number;
  dtMs: number;
  nowMs: number;
  sleeping: boolean;
  stage: Stage;
  decayMs?: number;
  tantrumTriggerMs?: number;
}

export function tickDiscipline(input: DisciplineTickInput): DisciplineState {
  const { state, hunger, happiness, dtMs, nowMs, sleeping, stage } = input;
  const decayMs = input.decayMs ?? DISCIPLINE_DECAY_MS;
  const tantrumTriggerMs = input.tantrumTriggerMs ?? TANTRUM_TRIGGER_MS;
  if (sleeping || stage === 'egg' || stage === 'dead') {
    return state;
  }
  const decay = dtMs / decayMs;
  let discipline = clamp(state.discipline - decay, 0, 100);

  // Tracking de "attention low" (hunger ou happiness abaixo do threshold).
  const lowNow = hunger < ATTENTION_THRESHOLD || happiness < ATTENTION_THRESHOLD;
  let attentionLowSince = state.attentionLowSince;
  if (lowNow && attentionLowSince === null) {
    attentionLowSince = nowMs - dtMs;
  } else if (!lowNow) {
    attentionLowSince = null;
  }

  let tantrum = state.tantrum;
  let tantrumStartedAt = state.tantrumStartedAt;
  if (
    !tantrum &&
    attentionLowSince !== null &&
    nowMs - attentionLowSince >= tantrumTriggerMs &&
    discipline < ATTENTION_THRESHOLD
  ) {
    tantrum = true;
    tantrumStartedAt = nowMs;
  }

  return { discipline, tantrum, tantrumStartedAt, attentionLowSince };
}

export function applyDiscipline(state: DisciplineState, _nowMs: number): DisciplineState {
  return {
    discipline: clamp(state.discipline + DISCIPLINE_BUMP, 0, 100),
    tantrum: false,
    tantrumStartedAt: null,
    attentionLowSince: state.attentionLowSince,
  };
}

/** Modificador de decay aplicado à hunger/happiness em runCareTick. */
export function disciplineModifier(d: number): number {
  // Em d=100 → -0.05 (decay 5% mais lento); em d=0 → +0.05 (5% mais rápido).
  return -(d - 50) / 1000;
}
